import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Truck,
  Construction,
  ShieldCheck,
  Clock,
  MapPin,
  Handshake,
  CheckCircle2,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { services, stats, projects, values, clients } from '../mock';

const iconMap = {
  truck: Truck,
  construction: Construction,
  'shield-check': ShieldCheck,
  clock: Clock,
  'map-pin': MapPin,
  handshake: Handshake,
  award: Award
};

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section
        className="relative py-24 md:py-32 overflow-hidden bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(15,23,42,0.85),rgba(15,23,42,0.85)), url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?auto=format&fit=crop&w=1920&q=80')"
        }}
      >
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-block mb-4 px-4 py-2 bg-orange-500/20 border border-orange-400/40 text-orange-300 rounded-full text-sm font-semibold">
              LEADER LOGISTIQUE INDUSTRIEL — GABON
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Transport industriel & location d'engins lourds —{' '}
              <span className="text-orange-400">
                Au service des leaders miniers et agro-industriels au Gabon
              </span>
            </h1>
            <p className="text-lg md:text-xl text-slate-200 mb-8 max-w-3xl">
              InfraOne achemine vos matières premières (minerai, bois, huile de
              palme, granulats) et met à votre disposition une flotte d'engins
              lourds avec ou sans chauffeurs — sur tous les axes du Gabon, 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                data-testid="hero-cta-quote"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                <Link to="/contact">
                  Demander un Devis
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                data-testid="hero-cta-fleet"
                className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg rounded-lg transition-all duration-300 bg-transparent"
              >
                <Link to="/fleet">Voir notre flotte</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-slate-900 text-white border-t border-slate-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-5xl font-bold text-orange-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-slate-300">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Présentation flash */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
              Spécialistes du <span className="text-orange-500">transport lourd</span>{' '}
              et de la <span className="text-orange-500">location d'engins</span>
            </h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Nous accompagnons les industriels gabonais — mines, agro-industrie,
              forestiers, BTP — avec une flotte moderne, des conducteurs
              expérimentés et un suivi temps réel. Contrats courts (chantiers) ou
              annuels, avec ou sans chauffeurs : nous bâtissons des solutions sur
              mesure.
            </p>
          </div>

          {/* 3 services clés */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((service) => {
              const Icon = iconMap[service.icon] || Truck;
              return (
                <Card
                  key={service.id}
                  data-testid={`home-service-${service.id}`}
                  className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-slate-200"
                >
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-500 transition-colors duration-300">
                      <Icon
                        size={28}
                        className="text-orange-500 group-hover:text-white transition-colors duration-300"
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      {service.short}
                    </p>
                    <Link
                      to="/services"
                      className="inline-flex items-center text-orange-500 font-medium group-hover:text-orange-600 transition-colors"
                    >
                      Détails du service
                      <ArrowRight size={16} className="ml-1" />
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Clients de référence */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-block mb-3 px-4 py-1 bg-orange-100 text-orange-600 rounded-full text-xs font-semibold tracking-wider">
              ILS NOUS FONT CONFIANCE
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Nos <span className="text-orange-500">clients de référence</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des partenariats long terme avec les leaders miniers,
              agro-industriels et portuaires du Gabon.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clients.map((client) => (
              <Card
                key={client.id}
                data-testid={`home-client-${client.id}`}
                className="hover:shadow-xl transition-all duration-300 border-t-4 border-t-orange-500"
              >
                <CardContent className="p-6">
                  <div className="text-xs uppercase tracking-wider text-orange-500 font-semibold mb-2">
                    {client.sector}
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">
                    {client.name}
                  </h3>
                  <p className="text-slate-600 text-sm">{client.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi InfraOne — valeurs */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi <span className="text-orange-500">InfraOne</span> ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Quatre engagements qui font la différence pour vos opérations.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => {
              const Icon = iconMap[value.icon] || ShieldCheck;
              return (
                <div
                  key={index}
                  className="bg-slate-50 p-6 rounded-xl hover:shadow-xl hover:bg-white transition-all duration-300 border border-transparent hover:border-orange-200"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <Icon size={24} className="text-orange-500" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-slate-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Réalisations récentes */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Réalisations <span className="text-orange-500">récentes</span>
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Études de cas illustrant notre savoir-faire terrain.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.slice(0, 3).map((project) => (
              <Card
                key={project.id}
                className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 bg-white"
              >
                <div className="h-44 bg-slate-900 relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 opacity-95"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Truck size={56} className="text-orange-400" />
                  </div>
                  <div className="absolute top-4 left-4">
                    <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {project.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {project.title}
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    {project.description}
                  </p>
                  <div className="space-y-1 border-t border-slate-200 pt-3">
                    {project.kpis.map((kpi, k) => (
                      <div key={k} className="flex items-center gap-2 text-xs text-slate-700">
                        <CheckCircle2 size={14} className="text-orange-500" />
                        <span>{kpi}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button
              asChild
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <Link to="/projects" data-testid="home-view-all-projects">
                Toutes nos réalisations
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Un convoi, une location, un contrat annuel ?
          </h2>
          <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
            Notre équipe commerciale revient vers vous sous 24h avec une
            proposition technique et tarifaire personnalisée.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
          >
            <Link to="/contact" data-testid="home-cta-contact">
              Demander un Devis
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
